"use client";

/**
 * Listings a person hid from the three-dot menu ("Bu elanı gizlədin",
 * owner 2026-10-26: a user asked to hide listings they are not interested
 * in). The server keeps the set (routes/hidden.py, the account or this
 * device) and leaves those listings out of the person's list and map; this
 * store only carries what was hidden in this tab, so the card goes at once
 * and comes back on undo.
 */
import { useSyncExternalStore } from "react";
import { API_BASE } from "@/lib/api";
import { getClientId } from "@/lib/client-id";
import type { ListingCard } from "@/types";

const ids = new Set<string>();
let snapshot: ReadonlySet<string> = new Set();
const subs = new Set<() => void>();
const EMPTY: ReadonlySet<string> = new Set();

function emit() {
  snapshot = new Set(ids);
  subs.forEach((f) => f());
}

function subscribe(f: () => void) {
  subs.add(f);
  return () => { subs.delete(f); };
}

/** The ids hidden in this tab (the grid leaves them out at once). */
export function useHiddenIds(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, () => snapshot, () => EMPTY);
}

export function markHidden(id: string) {
  if (ids.has(id)) return;
  ids.add(id);
  emit();
}

export function unmarkHidden(id: string) {
  if (ids.delete(id)) emit();
}

const headers = () => ({ "Content-Type": "application/json", "X-Client-Id": getClientId() });

export async function sendHide(id: string): Promise<void> {
  await fetch(`${API_BASE}/hidden`, {
    method: "POST", credentials: "include", headers: headers(), body: JSON.stringify({ listing_id: id }),
  });
}

export async function sendUnhide(id: string): Promise<void> {
  await fetch(`${API_BASE}/hidden/${encodeURIComponent(id)}`, {
    method: "DELETE", credentials: "include", headers: headers(),
  });
}

export interface HiddenItem {
  id: string;
  hidden_at: string;
  status: "active" | "removed" | "gone";
  card: ListingCard | null;
}

export async function loadHidden(signal?: AbortSignal): Promise<HiddenItem[]> {
  const r = await fetch(`${API_BASE}/hidden`, {
    credentials: "include", headers: { "X-Client-Id": getClientId() }, signal,
  });
  if (!r.ok) return [];
  const d = await r.json();
  return Array.isArray(d.items) ? d.items : [];
}
